import {
  DoNotDisturbOnOutlinedIcon,
  EllipsisText,
  ErrorOutlineRoundedIcon,
  Flex,
  PauseCircleOutlinedIcon,
  Text,
  type Theme,
  mergeSx,
  Tooltip,
} from '@shared/components';
import { type TableBodyRow } from '@shared/table-generator';
import { dayjs } from '@shared/utils';

import { EditQSetButton } from '../../containers';
import { ExamTypeIcon } from '../exam-type-icon';
import { RegistrationTypeLabel } from '../registration-type-label';

import {
  type ExamDistributionDataRowItem,
  type ExamDistributionRound,
  type ExamDistributionRoundInfo,
} from './base-monthly-exam-distribution-table';
import { type ExamEventCourseStatus, type ExamEventStatus } from './types';
type TableBodyRowChildren = TableBodyRow['children'][number];

export function getCellBaseStyle(width: string, padding?: number | string) {
  return {
    py: padding ?? 1,
    px: 1,
    width,
    height: '44px',
    minWidth: width,
    textAlign: 'center',
    '& > *': {
      mx: 'auto',
    },
  };
}

function getCellBackgroundColor(theme: Theme, examEventStatus: ExamEventStatus, examDate: Date) {
  const now = dayjs();

  const isTodayOrBefore = dayjs(examDate).isSameOrBefore(now, 'day');

  if (examEventStatus === 'CANCELED') {
    return theme.palette.error.lighter;
  }

  return isTodayOrBefore ? theme.palette.grey[100] : 'transparent';
}

function getCellColor(theme: Theme, examEventStatus: ExamEventStatus) {
  if (examEventStatus === 'CANCELED') {
    return theme.palette.error.dark;
  }
}

function renderExamEventCourseStatus(
  status: ExamEventCourseStatus | undefined,
  qSetCode: string | undefined,
) {
  switch (status) {
    case 'ALLOCATED':
      return <Text variant='body1Semibold'>{qSetCode}</Text>;
    case 'ASSIGNED':
      return <Text>{qSetCode}</Text>;
    case 'NO_ACCESS':
      return (
        <Tooltip title='No Access'>
          <DoNotDisturbOnOutlinedIcon
            sx={{ color: (theme) => theme.palette.grey[500], fontSize: '24px' }}
          />
        </Tooltip>
      );
    case 'NO_QSET':
      return (
        <Tooltip title='No QSet'>
          <ErrorOutlineRoundedIcon
            sx={{ color: (theme) => theme.palette.error.main, fontSize: '24px' }}
          />
        </Tooltip>
      );
    case 'PAUSE':
      return (
        <PauseCircleOutlinedIcon
          sx={{ color: (theme) => theme.palette.primary.main, fontSize: '24px' }}
        />
      );
    case 'UNASSIGNED':
    default:
      return <Text>-</Text>;
  }
}

function drawBorderBottom(theme: Theme, endOfMainRow: boolean, endOfSubRow: boolean) {
  if (endOfSubRow) {
    if (endOfMainRow) {
      return 'none';
    } else {
      return `1px solid ${theme.palette.grey[900]}`;
    }
  } else {
    return `1px solid ${theme.palette.grey[300]}`;
  }
}

function createExamEventCourseCells<T extends readonly string[]>(
  courses: T,
  examEventCourses: ExamDistributionRoundInfo<T>['examEventCourses'],
  endOfInfoRow: boolean,
  endOfDayRow: boolean,
) {
  return [
    // Exam Event Courses
    ...courses.map<TableBodyRowChildren>((course) => {
      const examEventCourse = examEventCourses.find((eventCourse) => eventCourse.course === course);
      return {
        children: (
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            {renderExamEventCourseStatus(examEventCourse?.status, examEventCourse?.qSetCode)}
          </Flex>
        ),
        align: 'center',
        sx: (theme) => ({
          borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
          ...getCellBaseStyle('80px'),
        }),
      };
    }),
  ];
}

function createInfoCells<T extends readonly string[]>(
  info: ExamDistributionRoundInfo<T>,
  courses: T,
  endOfInfoRow: boolean,
  endOfDayRow: boolean,
) {
  const {
    examCenterCode,
    registrationType,
    roundNumber,
    examEventCourses,
    examEventStatus,
    lastDownloadedAt,
    examType,
    examDate,
    location,
    canUpdate,
  } = info;

  const now = dayjs();
  const isTodayOrBefore = dayjs(examDate).isSameOrBefore(now, 'day');

  // hide actions if paper-based exam event and already downloaded at least once
  const alreadyDownloadedPaperBasedExamEvent = examType === 'PAPER' && !!lastDownloadedAt;

  const shouldHideActions =
    !canUpdate ||
    isTodayOrBefore ||
    examEventStatus === 'CANCELED' ||
    alreadyDownloadedPaperBasedExamEvent;

  return [
    // Registration Type
    {
      children: <RegistrationTypeLabel type={registrationType} />,
      align: 'center',
      sx: (theme) => ({
        borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
        backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
        color: getCellColor(theme, examEventStatus),
        ...getCellBaseStyle('56px'),
      }),
    },
    // Exam Type
    {
      children: (
        <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
          <ExamTypeIcon variant={examType} />
        </Flex>
      ),
      align: 'center',
      sx: (theme) => ({
        borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
        backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
        ...getCellBaseStyle('56px'),
      }),
    },
    // Exam Center Code
    {
      children: examCenterCode,
      align: 'center',
      sx: (theme) => ({
        ...theme.typography.body1Semibold,
        backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
        color: getCellColor(theme, examEventStatus),
        borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
        ...getCellBaseStyle('84px'),
      }),
    },
    // Round Number
    {
      children: roundNumber,
      align: 'center',
      sx: (theme) => ({
        ...theme.typography.body1Semibold,
        ...getCellBaseStyle('60px'),
        backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
        color: getCellColor(theme, examEventStatus),
        borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
      }),
    },
    // Location
    {
      children: <EllipsisText>{location}</EllipsisText>,
      align: 'center',
      sx: (theme) => ({
        ...theme.typography.body1Semibold,
        backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
        color: getCellColor(theme, examEventStatus),
        borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
        ...getCellBaseStyle('140px'),
      }),
    },
    // Exam Event Courses
    ...createExamEventCourseCells(courses, examEventCourses, endOfInfoRow, endOfDayRow).map(
      (cell) => ({
        ...cell,
        sx: mergeSx(cell.sx, (theme: Theme) => ({
          backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
          color: getCellColor(theme, examEventStatus),
        })),
      }),
    ),
    // More Action
    {
      children: shouldHideActions ? null : <EditQSetButton examEventId={info.examEventId} />,
      align: 'center',
      sx: (theme) => ({
        borderRight: 'none',
        ...getCellBaseStyle('48px'),
        backgroundColor: getCellBackgroundColor(theme, examEventStatus, examDate),
        borderBottom: drawBorderBottom(theme, endOfDayRow, endOfInfoRow),
      }),
    },
  ] as TableBodyRowChildren[];
}

// Time
function createTimeCell(
  timeFrom: string,
  timeTo: string,
  infoLength: number,
  isLastRound: boolean,
  endOfDayRow: boolean,
) {
  return {
    children: `${timeFrom} - ${timeTo}`,
    rowSpan: infoLength + 1,
    sx: (theme) => ({
      alignContent: infoLength > 1 ? 'start' : 'center',
      borderBottom: drawBorderBottom(theme, endOfDayRow, isLastRound),
      ...getCellBaseStyle('104px', infoLength > 1 ? theme.spacing(3) : theme.spacing(1)),
    }),
  } as TableBodyRowChildren;
}

function createRoundRows<T extends readonly string[]>(
  round: ExamDistributionRound<T>,
  courses: T,
  isLastRound: boolean,
  endOfDayRow: boolean,
) {
  const { info, time } = round;

  return [
    // Time row
    {
      children: [createTimeCell(time.from, time.to, info.length, isLastRound, endOfDayRow)],
    },
    // Info rows
    ...info.map<TableBodyRow>((infoData, infoIndex) => {
      const endOfInfoRow = isLastRound && infoIndex === info.length - 1;
      return {
        children: createInfoCells(infoData, courses, endOfInfoRow, endOfDayRow),
      };
    }),
  ];
}

// Day & Date
// NOTE: rowCount > 3 is for handling rowSpan > 1
function createDayDateCells(day: string, date: string, roundCount: number, endOfDayRow: boolean) {
  return [
    {
      children: day,
      align: 'center',
      rowSpan: roundCount,
      sx: (theme) => ({
        ...theme.typography.body1Semibold,
        borderBottom: drawBorderBottom(theme, endOfDayRow, true),
        alignContent: roundCount > 3 ? 'start' : 'center',
        ...getCellBaseStyle('52px', roundCount > 3 ? theme.spacing(3) : theme.spacing(1)),
      }),
    },
    {
      children: date,
      align: 'center',
      rowSpan: roundCount,
      sx: (theme) => ({
        ...theme.typography.body1Semibold,
        borderBottom: drawBorderBottom(theme, endOfDayRow, true),
        alignContent: roundCount > 3 ? 'start' : 'center',
        ...getCellBaseStyle('60px', roundCount > 3 ? theme.spacing(3) : theme.spacing(1)),
      }),
    },
  ] as TableBodyRowChildren[];
}

export function createDayRows<T extends readonly string[]>(
  dayData: ExamDistributionDataRowItem<T>,
  courses: T,
  endOfDayRow: boolean,
) {
  const { date, day, rounds } = dayData;
  const roundCount = rounds.reduce((acc, cur) => acc + (cur.info.length + 1), 1);

  const dayDateRow: TableBodyRow = {
    children: createDayDateCells(day, date, roundCount, endOfDayRow),
    sx: (theme) => ({
      backgroundColor: theme.palette.grey[200],
    }),
  };

  return [
    dayDateRow,
    ...rounds.flatMap((round, roundIndex) => {
      const isLastRound = roundIndex === rounds.length - 1;
      return createRoundRows(round, courses, isLastRound, endOfDayRow);
    }),
  ];
}
